<template>
  <form
    :ref="formRefName"
    @submit.prevent="submit"
  >
    <party-type-selector
      v-if="
        isProsecutorOrDefendant(selectedCaseParty) && !isCasePartyRepresentative && isAddSubMode
      "
      :form-model="selectedCaseParty"
      :section-title="partyTypeSelectionTitle"
      :party-types-list="partyTypesList" 
    ></party-type-selector>
    <naj-box
      v-if="isAllowedAsRepresentativeForOrganizationOwner(selectedCaseParty) && (isPersonNotInheritor(selectedCaseParty) || isOrganizationOwner(selectedCaseParty)) && !hidePersonalSections && isAddSubMode"
      no-header
    >
      <v-col
        cols="12"
        lg="3"
        class="my-auto"
      >
        <naj-input
          :type-id="formFieldType.checkbox"
          :label="local.partyTypeSelection.isOrganizationOwner"
          :value.sync="selectedCaseParty.isOrganizationOwner"
        ></naj-input>
      </v-col>
    </naj-box>
    <person-type
      v-if="isPersonNotInheritor(selectedCaseParty)"
      :form-model="selectedCaseParty"
      :is-representative="isCasePartyRepresentative"
      :required="isPartyDataRequired"
      :hide-personal-sections="hidePersonalSections"
      :show-more-address-details="showMoreAddressDetails"
      :allow-address-check="allowAddressCheck"
      :party-roles="partyRolesList"
      :existing-parties="caseParties"
      :show-party-role="!isCasePartyRepresentative &&(isAddSubMode || isEditSubMode)" 
    ></person-type>

    <firm-type
      v-if="isFirm(selectedCaseParty)"
      :form-model="selectedCaseParty"
      :is-representative="isCasePartyRepresentative"
      :required="isPartyDataRequired"
      :show-more-address-details="showMoreAddressDetails"
      :allow-address-check="allowAddressCheck"
      :show-commercial-registration-type-id="true"
      :check-commercial-register-number-status="checkCommercialRegisterNumberStatus"
      :party-roles="partyRolesList"
      :existing-parties="caseParties"
      :show-party-role="!isCasePartyRepresentative &&(isAddSubMode || isEditSubMode)" 
    ></firm-type>

    <cessation-type
      v-if="isCessation(selectedCaseParty)"
      :form-model="selectedCaseParty"
      :required="true"
    ></cessation-type>

    <liquidator-type
      v-if="isLiquidatorType(selectedCaseParty)"
      :form-model="selectedCaseParty"
      :required="true"
    ></liquidator-type>

    <judicial-guard-type
      v-if="isJudicialGuardType(selectedCaseParty)"
      :form-model="selectedCaseParty"
      :required="true"
    ></judicial-guard-type>

    <organization-type
      v-if="isOrganization(selectedCaseParty)"
      :form-model="selectedCaseParty"
      :is-representative="isCasePartyRepresentative"
      :required="isPartyDataRequired"
      :hide-personal-sections="hidePersonalSections"
      :show-more-address-details="showMoreAddressDetails"
      :allow-address-check="allowAddressCheck"
    ></organization-type>

    <government-type
      v-if="isGovernment(selectedCaseParty)"
      :form-model="selectedCaseParty"
      :is-representative="isCasePartyRepresentative"
      :required="true"
    ></government-type>

    <association-type
      v-if="isAssociation(selectedCaseParty)"
      :form-model="selectedCaseParty"
      :is-representative="isCasePartyRepresentative"
      :hide-personal-sections="hidePersonalSections"
      :required="true"
    ></association-type>

    <embassy-type
      v-if="isEmbassy(selectedCaseParty)"
      :form-model="selectedCaseParty"
      :required="true"
    ></embassy-type>

    <institution-without-commercial-number-type
      v-if="isInstitutionWithoutCommercialRegisterNo(selectedCaseParty)"
      :form-model="selectedCaseParty"
      :required="true"
    ></institution-without-commercial-number-type>

    <human-society-type
      v-if="isHumanSociety(selectedCaseParty)"
      :form-model="selectedCaseParty"
      :required="true"
    ></human-society-type>

    <foreign-company-type
      v-if="isForeignCompany(selectedCaseParty)"
      :form-model="selectedCaseParty"
      :required="true"
    ></foreign-company-type>

    <inheritor-type
      v-if="isInheritor(selectedCaseParty)"
      :form-model="selectedCaseParty"
      :required="true"
      @inheritor-validation-changed="onInheritorValidationChanged"
    ></inheritor-type>

    <contact-form
      v-if="!hidePersonalSections && selectedCaseParty.partyType && isPersonNotInheritor(selectedCaseParty)"
      :form-model="selectedCaseParty.contact"
    ></contact-form>

    <v-row>
      <v-col
        cols="12"
        class="component-actions text-left"
      >
        <naj-button
          outlined
          @click="cancelCasePartyForm"
        >
          {{ local.common.back }}
        </naj-button>
        <naj-button
          type="submit"
          color="primary"
        >
          {{
            isEditSubMode ? local.common.update : local.common.add
          }}
        </naj-button>
      </v-col>
    </v-row>
  </form>
</template>

<script>
import PartyTypeSelector from './party-type-selector';
import PersonType from './party-types/person-type';
import FirmType from './party-types/firm-type';
import cessationType from './party-types/cessation-type.vue';
import LiquidatorType from './party-types/liquidator-type.vue';
import JudicialGuardType from './party-types/judicial-guard-type.vue';
import OrganizationType from './party-types/organization-type';
import GovernmentType from './party-types/government-type';
import EmbassyType from './party-types/embassy-type';
import HumanSocietyType from './party-types/human-society-type';
import ForeignCompanyType from './party-types/foreign-company-type';
import AssociationType from './party-types/association-type';
import InstitutionWithoutCommercialNumberType from './party-types/institution-without-commercial-number-type';
import InheritorType from './party-types/inheritor-type.vue';
import ContactForm from './party-types/forms/contact-form';
import { casePartyRoleByIdEnum, casePartyRoleEnum } from '@/helpers/enum.js';
import {
  isProsecutor,
  isRepresentative,
  isAllowedAsRepresentativeForOrganizationOwner,
  isProsecutorOrDefendant,
  isOrganizationOwner,
  isFirm,
  isCessation,
  isLiquidatorType,
  isJudicialGuardType,
  isOrganization,
  isGovernment,
  isEmbassy,
  isAssociation,
  isInstitutionWithoutCommercialRegisterNo,
  isHumanSociety,
  isForeignCompany,
  isAgent,
  isLawyer,
  isRepresentativeRole,
  isRegent,
  isGuardian,
  isInheritor,
  isPersonNotInheritor,
  isDefendantOrDefendantRepresentative,
} from '../../helpers/utils';
import { scrollIntoInValidInput } from '@/helpers/utils.js';
import { createNamespacedHelpers } from 'vuex';
import usePartyAttributesMixin from '@/modules/lawsuit/mixins/usePartyAttributesMixin';

const { mapActions, mapState, mapGetters } = createNamespacedHelpers('LawsuitModule');

export default {
  components: {
    PersonType,
    FirmType,
    cessationType,
    LiquidatorType,
    JudicialGuardType,
    OrganizationType,
    GovernmentType,
    EmbassyType,
    HumanSocietyType,
    ForeignCompanyType,
    PartyTypeSelector,
    AssociationType,
    InstitutionWithoutCommercialNumberType,
    ContactForm,
    InheritorType,
  },
  mixins: [usePartyAttributesMixin],
  props: {
    checkCommercialRegisterNumberStatus: {
      require: false,
      default: false,
      type: Boolean,
    },
  },
  data() {
    return {
      isSubmitted: false,
      formRefName: 'selectedCaseParty',
      visibilityStateOfPersonalSectionsFreezed: false,
      visibilityStateOfPersonalSections: null,
      isInheritorValidated: false,
      casePartyRoleByIdEnum,
      casePartyRoleEnum,
      isProsecutor,
      isRepresentative,
      isAllowedAsRepresentativeForOrganizationOwner,
      isProsecutorOrDefendant,
      isOrganizationOwner,
      isFirm,
      isCessation,
      isLiquidatorType,
      isJudicialGuardType,
      isOrganization,
      isGovernment,
      isEmbassy,
      isAssociation,
      isInstitutionWithoutCommercialRegisterNo,
      isHumanSociety,
      isForeignCompany,
      isAgent,
      isLawyer,
      isRepresentativeRole,
      isRegent,
      isGuardian,
      isInheritor,
      isPersonNotInheritor,
      isDefendantOrDefendantRepresentative,
    };
  },
  computed: {
    ...mapState({
      selectedCaseParty: state => state.selectedCaseParty,
      casePartiesList: state => state.lawsuitRequest.caseParties,
      caseData: state => state.lawsuitRequest.case,
    }),
    ...mapGetters([
      'selectedCaseType',
      'isNewMode',
      'isEditMode',
      'isAddSubMode',
      'isEditSubMode',
      'isSelectedCasePartyExistsBeforeAsCaseParty',
      'isSelectedCasePartyExistsBeforeAsRepresentative',
      'isSelectedCasePartyCurrentUserAndProsecutorOrProsecutorRepresentative',
      'isSelectedCasePartyCurrentUserOrProsecutorOrProsecutorRepresentative',
      'prosecutors',
      'defendants',
    ]),
    caseTypeId()      { return this.selectedCaseType?.id; },
    legalPositionId() { return this.selectedCaseParty.casePartyRole; },
    partyTypeId()     { return this.selectedCaseParty.partyType; },
    caseParties() {
      if (this.selectedCaseParty.casePartyRole === this.casePartyRoleEnum.prosecutor) {
        return Object.values(this.prosecutors);
      } else if (this.selectedCaseParty.casePartyRole === this.casePartyRoleEnum.defendant) {
        return Object.values(this.defendants);
      }
      return [];
    },
    showMoreAddressDetails() {
      return this.isSelectedCasePartyCurrentUserAndProsecutorOrProsecutorRepresentative;
    },
    allowAddressCheck() {
      return this.isSelectedCasePartyCurrentUserOrProsecutorOrProsecutorRepresentative || this.isDefendantOrDefendantRepresentative(this.selectedCaseParty);
    },
    selectedCasePartyRoleId() {
      return this.selectedCaseParty.casePartyRole;
    },
    isPartyDataRequired() {
      return (
        this.isProsecutor(this.selectedCaseParty) || (this.isRepresentative(this.selectedCaseParty) && !this.isRepresentativeForLiquidatorOrJudicialGuard)
      );
    },
    isRepresentativeForLiquidatorOrJudicialGuard() {
      return (this.selectedCaseParty.relatedPartiesInfo || []).some(party => party.casePartyRole === casePartyRoleEnum.liquidator || party.casePartyRole === casePartyRoleEnum.judicialGuard);
    },
    partyTypeSelectionTitle() {
      return (
        this.local.common.the +
        (this.local.common[this.casePartyRoleByIdEnum[this.selectedCasePartyRoleId]] ||
          this.local.common.casePartyRoles[this.casePartyRoleByIdEnum[this.selectedCasePartyRoleId]])
      );
    },
    isCasePartyRepresentative() {
      const casePartyIndex = this.selectedCaseParty.relatedPartiesInfo.findIndex(
        rp => rp.casePartyObjectKey === this.selectedCaseParty.casePartyObjectKey
      );
      return casePartyIndex !== -1;
    },
    hidePersonalSections() {
      this.getHidePersonalSectionsState();
      return this.visibilityStateOfPersonalSections;
    },
  },
  watch: {
    'selectedCaseParty.isOrganizationOwner'() {
      this.clearObject(this.selectedCaseParty, [
        'isOrganizationOwner',
        'objectKey',
        'casePartyObjectKey',
        'partyType',
        'casePartyRole',
        'relatedPartiesInfo',
      ]);
    },
    partyTypeId(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.selectedCaseParty.subPartyRoleId = null;
      }
    },
  },
  mounted() {
    this.$refs[this.formRefName].scrollIntoView();
    window.scroll(0, 0);
    setTimeout(() => {
      this.visibilityStateOfPersonalSectionsFreezed = true;
    }, 0);
  },
  methods: {
    ...mapActions([
      'saveCasePartyInNewMode',
      'saveCasePartyRepresentativeInNewMode',
      'saveCasePartyInEditMode',
      'saveCasePartyRepresentativeInEditMode',
      'cancelCasePartyForm',
    ]),
    clearObject(state, exceptArr) {
      for (const key in state) {
        if (Object.prototype.hasOwnProperty.call(state, key) && exceptArr.indexOf(key) === -1) {
          if (typeof state[key] === 'object') {
            this.clearObject(state[key], exceptArr);
          } else {
            state[key] = undefined;
          }
        }
      }
    },
    getHidePersonalSectionsState() {
      const state =
        (this.isCasePartyRepresentative && this.isSelectedCasePartyExistsBeforeAsCaseParty) ||
        (this.isCasePartyRepresentative &&
          this.isSelectedCasePartyExistsBeforeAsRepresentative &&
          this.isAddSubMode);
      this.visibilityStateOfPersonalSections = this.visibilityStateOfPersonalSectionsFreezed
        ? this.visibilityStateOfPersonalSections
        : state;
    },
    submit() {
      this.isSubmitted = true;

      this.$validator.validateAll()
        .then((valid) => {
          if (!valid) {
            this.isSubmitted = false;
            scrollIntoInValidInput(this.errors);
            return false;
          }
          this.updateRelatedPartiesInfo();
          if (this.isInheritor(this.selectedCaseParty) && !this.isInheritorValidated) {
            this.toaster.error(this.local.inheritorData.checkInheritorData);
            this.isSubmitted = false;
            return false;
          }

          if (this.isNewMode) {
            if (
              this.isProsecutorOrDefendant(this.selectedCaseParty) &&
              !this.isRepresentative(this.selectedCaseParty)
            ) {
              this.saveCasePartyInNewMode();
            } else if (this.isRepresentative(this.selectedCaseParty)) {
              this.saveCasePartyRepresentativeInNewMode();
            }
          } else if (this.isEditMode) {
            if (
              this.isProsecutorOrDefendant(this.selectedCaseParty) &&
              !this.isRepresentative(this.selectedCaseParty)
            ) {
              this.saveCasePartyInEditMode();
            } else if (this.isRepresentative(this.selectedCaseParty)) {
              this.saveCasePartyRepresentativeInEditMode();
            }
          }
        });
    },
    updateRelatedPartiesInfo() {
      const { objectKey: selectedKey, socialId, socialIdType } = this.selectedCaseParty;

      this.casePartiesList.forEach((mainParty) => {
        if (Array.isArray(mainParty.relatedPartiesInfo)) {
          mainParty.relatedPartiesInfo.forEach((relatedParty) => {
            if (relatedParty.casePartyObjectKey === selectedKey) {
              relatedParty.socialId = socialId;
              relatedParty.socialIdType = socialIdType;
              if (
                this.isEditSubMode &&
                [this.casePartyRoleEnum.agent,
                this.casePartyRoleEnum.lawyer,
                this.casePartyRoleEnum.representative].includes(relatedParty.casePartyRole)
              ) {
                relatedParty.isApprovedFromMofa = undefined;
                relatedParty.isApprovedFromMoj = undefined;
                relatedParty.isVerified = undefined;
              }
            }
          });
        }
      });
    },
    onInheritorValidationChanged(isValid) {
      this.isInheritorValidated = isValid;
    },
  },
  reactiveProvide: {
    name: 'formContext',
    include: ['isSubmitted'],
  },
};
</script>
